import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { ArrowLeft, Shield, Clock, User } from "lucide-react";
import { format } from "date-fns";
import Footer from "@/components/Footer";

interface LogEntry {
  id: string;
  created_at: string;
  action: string;
  details: any;
  pin_used: boolean;
  profiles?: {
    full_name: string;
    email: string;
  };
}

export default function AdminLogs() {
  const navigate = useNavigate();
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkAdminAndFetchLogs();
  }, []);

  const checkAdminAndFetchLogs = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate("/auth");
      return;
    }

    const { data: roles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id);

    const isAdmin = roles?.some(r => r.role === "admin" || r.role === "manager" || r.role === "super_admin");
    
    if (!isAdmin) {
      toast.error("Access denied - Admin, Manager, or Super Admin only");
      navigate("/dashboard");
      return;
    }

    fetchLogs();
  };

  const fetchLogs = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("admin_logs")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);

    if (error) {
      toast.error("Failed to fetch logs");
      console.error(error);
      setLoading(false);
      return;
    }

    // Fetch user profiles separately
    const userIds = [...new Set(data?.map(log => log.user_id).filter(Boolean) || [])];
    const { data: profilesData } = await supabase
      .from("profiles")
      .select("id, full_name, email")
      .in("id", userIds);

    const enrichedLogs = data?.map(log => ({
      ...log,
      profiles: profilesData?.find(p => p.id === log.user_id) || null,
    })) || [];

    setLogs(enrichedLogs as LogEntry[]);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <div className="flex-1 container mx-auto p-4 sm:p-6 max-w-6xl">
        <div className="flex gap-4 mb-6">
          <Button onClick={() => navigate("/dashboard")} variant="outline">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-primary" />
              Admin Activity Logs
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center gap-2 p-8">
                <Shield className="w-5 h-5 text-primary animate-pulse" />
                <span className="text-sm">Loading logs...</span>
              </div>
            ) : logs.length === 0 ? (
              <div className="text-center p-8 text-muted-foreground">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No activity logs found</p>
              </div>
            ) : (
              <div className="space-y-3 max-h-[calc(100vh-250px)] overflow-y-auto">
                {logs.map((log) => (
                  <div
                    key={log.id}
                    className="p-4 border rounded-lg bg-card hover:bg-accent/5 transition-colors"
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                        <span className="text-sm text-muted-foreground">
                          {format(new Date(log.created_at), "PPP 'at' p")}
                        </span>
                      </div>
                      {log.pin_used && (
                        <span className="inline-flex items-center gap-1 text-xs bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 px-2 py-1 rounded w-fit">
                          <Shield className="w-3 h-3" />
                          PIN Used
                        </span>
                      )}
                    </div>
                    
                    <div className="flex items-start gap-2 mb-2">
                      <User className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium">
                          {log.profiles?.full_name || "Unknown User"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {log.profiles?.email || "No email"}
                        </p>
                      </div>
                    </div>

                    <div className="pl-6 space-y-1">
                      <p className="text-sm font-semibold text-foreground">
                        Action: {log.action}
                      </p>
                      {log.details && (
                        <div className="text-xs text-muted-foreground bg-muted p-2 rounded mt-2">
                          <pre className="whitespace-pre-wrap break-words">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      <Footer />
    </div>
  );
}
